import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, VStack, HStack, Text, Badge } from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiAlertTriangle } from 'react-icons/fi';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Dog } from '@/types/dog';
import { VACCINATION_TYPE_LABELS } from '@/types/vaccination';
import { useVaccinationStore } from '@/stores/vaccinationStore';
import {
  getAlertLevelFromDueDate,
  getAlertLabel,
  getAlertColor,
  VACCINATION_UPCOMING_DAYS,
} from '@/utils/vaccinationAlert';

interface VaccinationAlertSectionProps {
  userId: string;
  /** 個体名の解決に使用する個体一覧 */
  dogs: Dog[];
}

/**
 * 個体一覧ページ上部に表示する、ワクチン期限の折りたたみアラートセクション。
 *
 * 期限切れ・期限間近（90 日以内）の接種記録を一覧表示する。
 */
export const VaccinationAlertSection: FC<VaccinationAlertSectionProps> = ({
  userId,
  dogs,
}) => {
  const navigate = useNavigate();
  const { upcomingVaccinations, fetchUpcoming } = useVaccinationStore();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUpcoming(userId, VACCINATION_UPCOMING_DAYS);
    }
  }, [userId, fetchUpcoming]);

  const dogNameById = useMemo(() => {
    const map = new Map<string, Dog>();
    dogs.forEach((dog) => map.set(dog.id, dog));
    return map;
  }, [dogs]);

  // 期限切れを先頭に並べた一覧
  const items = useMemo(() => {
    return upcomingVaccinations
      .filter((record) => record.nextDueDate)
      .map((record) => {
        const { level, daysUntilDue } = getAlertLevelFromDueDate(
          record.nextDueDate!.toDate()
        );
        return { record, level, daysUntilDue };
      })
      .filter((item) => item.level !== 'none');
  }, [upcomingVaccinations]);

  if (items.length === 0) {
    return null;
  }

  const overdueCount = items.filter((i) => i.level === 'overdue').length;
  const upcomingCount = items.filter((i) => i.level === 'upcoming').length;

  return (
    <Box bg="white" borderRadius="lg" shadow="md" overflow="hidden">
      <HStack
        p={4}
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsOpen((prev) => !prev)}
        _hover={{ bg: 'gray.50' }}
      >
        <HStack gap={2}>
          <FiAlertTriangle color="#D69E2E" />
          <Text fontWeight="bold">ワクチン期限アラート</Text>
          {overdueCount > 0 && (
            <Badge colorPalette="red">期限切れ {overdueCount}件</Badge>
          )}
          {upcomingCount > 0 && (
            <Badge colorPalette="yellow">期限間近 {upcomingCount}件</Badge>
          )}
        </HStack>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </HStack>

      {isOpen && (
        <VStack align="stretch" gap={2} px={4} pb={4}>
          {items.map(({ record, level, daysUntilDue }) => {
            const dog = dogNameById.get(record.dogId);
            const mainName = dog?.officialName || dog?.name || '個体';
            return (
              <HStack
                key={record.id}
                p={3}
                bg="gray.50"
                borderRadius="md"
                justify="space-between"
                cursor="pointer"
                onClick={() => navigate(`/dogs/${record.dogId}`)}
                _hover={{ bg: 'gray.100' }}
              >
                <VStack align="start" gap={0.5}>
                  <Text fontSize="sm" fontWeight="medium">
                    {mainName}
                  </Text>
                  <HStack gap={2}>
                    <Badge colorPalette="blue" size="sm">
                      {VACCINATION_TYPE_LABELS[record.type]}
                    </Badge>
                    {record.nextDueDate && (
                      <Text fontSize="xs" color="gray.600">
                        期限:{' '}
                        {format(record.nextDueDate.toDate(), 'yyyy年M月d日', {
                          locale: ja,
                        })}
                      </Text>
                    )}
                  </HStack>
                </VStack>
                <Badge colorPalette={getAlertColor(level)}>
                  {getAlertLabel(level)}
                  {level === 'overdue'
                    ? `（${Math.abs(daysUntilDue)}日超過）`
                    : `（残り${daysUntilDue}日）`}
                </Badge>
              </HStack>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default VaccinationAlertSection;
